import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Edit from "../../../../assets/images/edit.svg";
import {
  ChipOptionsDataType,
  FormController,
  MuiBox,
  MuiDateField,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
} from "../../../../components";
import MuiChipWithPopover from "../../../../components/chipWithPopover/MuiChipWithPopover";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { MuiLink } from "../../../../components/link/MuiLink";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import { MODULE_PERMISSIONS, PAGE_URLS } from "../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import { MAX_LENGTH_50 } from "../../../../helpers/constants/validations";
import { LabelValue } from "../../../../helpers/types/common";
import {
  compareDates,
  convertToUserFormat,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import {
  OptionListType,
  SearchProgramTemplateResultFormKey,
  SearchProgramTemplateResultRowValue,
} from "../../modelsAndSchemas/Models";
import { programTemplateSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ProgramTemplateSearchResultWrapper } from "./ProgramTemplateSearchResult.style";

interface ProgramTemplateSearchResultProps {
  rows: SearchProgramTemplateResultRowValue[];
  isSearch: boolean;
  pageId: string;
}

export const ProgramTemplateSearchResult: FC<
  ProgramTemplateSearchResultProps
> = ({ rows, isSearch, pageId }) => {
  const tableRef = React.useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const { hasModulePermission } = usePermissions();

  const countryRef = React.useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );
  // updated sitecore data
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    SearchProgramTemplateResultRowValue[]
  >([]);
  const [uniqueSolutionCategories, setUniqueSolutionCategories] = useState<
    LabelValue[]
  >([]);
  const [uniqueSolutionCategoryLobs, setUniqueSolutionCategoryLobs] = useState<
    LabelValue[]
  >([]);
  const [uniqueProgramFrameWorks, setUniqueProgramFrameWorks] = useState<
    LabelValue[]
  >([]);
  const [uniqueLineOfBusinesses, setUniqueLineOfBusinesses] = useState<
    LabelValue[]
  >([]);
  const [uniqueCountries, setUniqueCountries] = useState<LabelValue[]>([]);
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
    fetchComponentTitle,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const navigate = useNavigate();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    if (tableRef.current) {
      tableRef.current.resetPagination(shouldResetRowsPerPage);
    }
  };

  useEffect(() => {
    resetPagination(true);
    countryRef.current?.resetValues();
    const myData = [...rows];

    // Extract unique solution categories and solution category LOBs
    const uniqueCategories = Array.from(
      new Set(myData.map((item) => item.solutionCategory)),
    ).map((category) => ({ value: category, label: category }));
    const uniqueCategoryLobs = Array.from(
      new Set(myData.map((item) => item.solutionCategoryLob)),
    ).map((categoryLob) => ({ value: categoryLob, label: categoryLob }));
    const uniqueProgramFrameWorks = Array.from(
      new Set(myData.map((item) => item.programFrameWork)),
    ).map((programFrameWork) => ({
      value: programFrameWork,
      label: programFrameWork,
    }));
    const uniqueLineOfBusinesses = Array.from(
      new Set(myData.map((item) => item.lineOfBusiness)),
    ).map((lineOfBusiness) => ({
      value: lineOfBusiness,
      label: lineOfBusiness,
    }));
    const uniqueCountries = Array.from(
      new Set(
        myData
          .map((item) => item?.countryName ?? [])
          .reduce((acc, val) => acc.concat(val), []),
      ),
    ).map((country) => ({ value: country, label: country }));

    setUniqueSolutionCategories(uniqueCategories);
    setUniqueSolutionCategoryLobs(uniqueCategoryLobs);
    setUniqueProgramFrameWorks(uniqueProgramFrameWorks);
    setUniqueLineOfBusinesses(uniqueLineOfBusinesses);
    setUniqueCountries(uniqueCountries);
    reset();
  }, [rows]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData: SearchProgramTemplateResultRowValue[] = JSON.parse(
        JSON.stringify(rows),
      );
      localData.forEach((data) => {
        data.statusKey = data.status;
        data.status = fetchValueByKeyOfDropdown(
          programTemplateSiteCoreKeys.searchFormKey,
          programTemplateSiteCoreKeys.statusList,
          data.status,
        );
      });
      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);
    }
  }, [rows, siteCoreLoading]);

  const { control, watch, setValue, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [SearchProgramTemplateResultFormKey.name]: "",
      [SearchProgramTemplateResultFormKey.lineOfBusiness]: "",
      [SearchProgramTemplateResultFormKey.programFrameWork]: "",
      [SearchProgramTemplateResultFormKey.solutionCategory]: "",
      [SearchProgramTemplateResultFormKey.solutionCategoryLob]: "",
      [SearchProgramTemplateResultFormKey.status]: "",
      [SearchProgramTemplateResultFormKey.countryId]: [],
      [SearchProgramTemplateResultFormKey.countryName]: [],
      [SearchProgramTemplateResultFormKey.createdDate]: "",
    },
  });

  const getNameFromValue = (list: OptionListType, id: string) => {
    const lob = list.find((lob) => lob.value === id);
    return lob ? lob.label : id;
  };

  useEffect(() => {
    const {
      name,
      lineOfBusiness,
      programFrameWork,
      solutionCategory,
      solutionCategoryLob,
      status,
      countryName,
      createdDate,
    } = getValues();

    if (isNotEmpty(allUpdatedRows)) {
      const myData = allUpdatedRows.filter((item) => {
        const nameMatch = item.name.toLowerCase().includes(name.toLowerCase());

        const lineOfBusinessMatch = item.lineOfBusiness
          .toLowerCase()
          .includes(
            getNameFromValue(
              uniqueLineOfBusinesses,
              lineOfBusiness,
            ).toLowerCase(),
          );

        const programFrameWorkMatch = item.programFrameWork
          .toLowerCase()
          .includes(
            getNameFromValue(
              uniqueProgramFrameWorks,
              programFrameWork,
            ).toLowerCase(),
          );

        const solutionCategoryMatch = item.solutionCategory
          .toLowerCase()
          .includes(solutionCategory.toLowerCase());

        const solutionCategoryLobMatch = item.solutionCategoryLob
          .toLowerCase()
          .includes(solutionCategoryLob.toLowerCase());

        const statusAll = fetchValueByKeyOfDropdown(
          programTemplateSiteCoreKeys.searchFormKey,
          programTemplateSiteCoreKeys.statusList,
          commonSiteCoreKeys.statusAll,
        );

        const statusMatch =
          status === "" ||
          status === statusAll ||
          item.status.toLowerCase() === status.toLowerCase();

        const countryMatch =
          countryName.length === 0 ||
          item?.countryName?.some((id) =>
            countryName.some(
              (country: string) => id.toLowerCase() === country.toLowerCase(),
            ),
          );

        let isDateMatch = false;

        if (createdDate !== "") {
          isDateMatch = compareDates(
            createdDate ?? "",
            item?.createdDate ?? "",
            getValueOrBlank(userCountryDateFormat),
          );
        } else {
          isDateMatch = true;
        }

        return (
          nameMatch &&
          lineOfBusinessMatch &&
          programFrameWorkMatch &&
          solutionCategoryMatch &&
          solutionCategoryLobMatch &&
          statusMatch &&
          (countryName.length === 0 || countryMatch) &&
          isDateMatch
        );
      });

      setFilteredResults([...myData]);
      resetPagination();
    }
  }, [
    watch(SearchProgramTemplateResultFormKey.name),
    watch(SearchProgramTemplateResultFormKey.lineOfBusiness),
    watch(SearchProgramTemplateResultFormKey.programFrameWork),
    watch(SearchProgramTemplateResultFormKey.solutionCategory),
    watch(SearchProgramTemplateResultFormKey.solutionCategoryLob),
    watch(SearchProgramTemplateResultFormKey.status),
    watch(SearchProgramTemplateResultFormKey.countryName),
    watch(SearchProgramTemplateResultFormKey.createdDate),
  ]);

  const columns = [
    {
      id: SearchProgramTemplateResultFormKey.name,
      label: fetchLabel(
        programTemplateSiteCoreKeys.searchFormKey,
        programTemplateSiteCoreKeys.programTemplateNameLabel,
      ),
      className: "cell-program-template-name",
      render: (row: any) =>
        !row.name ? null : (
          <MuiLink
            href={`${PAGE_URLS.VIEW_PROGRAM_TEMPLATE}/${row.id}`}
            onClick={(e) => {
              if (!hasModulePermission(MODULE_PERMISSIONS.PROGRAM_TEMPLATE_VIEW)) {
                e.preventDefault();
              }
            }}
            isOpenNewTab
            id={`${pageId}-view-template`}
            data-testid="view-template-link"
          >
            <MuiBox>
              <MuiEllipseLabel
                data-testid="view-template-link"
                labelTypoClassName="Link"
                text={row.name}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
      isSortVisible: true,
    },
    {
      id: SearchProgramTemplateResultFormKey.lineOfBusiness,
      label: fetchLabel(
        programTemplateSiteCoreKeys.searchFormKey,
        programTemplateSiteCoreKeys.productLineLabel,
      ),
      isSortVisible: true,
      className: "cell-line-of-business",
    },
    {
      id: SearchProgramTemplateResultFormKey.programFrameWork,
      label: fetchLabel(
        programTemplateSiteCoreKeys.result,
        programTemplateSiteCoreKeys.programFrameworkLabel,
      ),
      isSortVisible: true,
      className: "cell-program-class",
    },
    {
      id: SearchProgramTemplateResultFormKey.solutionCategory,
      label: fetchLabel(
        programTemplateSiteCoreKeys.result,
        programTemplateSiteCoreKeys.solutionCategoryHeader,
      ),
      isSortVisible: true,
      className: "cell-solution-category",
    },
    {
      id: SearchProgramTemplateResultFormKey.solutionCategoryLob,
      label: fetchLabel(
        programTemplateSiteCoreKeys.result,
        programTemplateSiteCoreKeys.solutionSubCategory,
      ),
      isSortVisible: true,
    },
    {
      id: SearchProgramTemplateResultFormKey.status,
      label: fetchLabel(
        programTemplateSiteCoreKeys.searchFormKey,
        programTemplateSiteCoreKeys.statusLabel,
      ),
      isSortVisible: true,
      className: "cell-status",
      render: (row: any) => (
        <StatusBadge status={row.statusKey} label={row.status} />
      ),
    },
    {
      id: SearchProgramTemplateResultFormKey.countryName,
      label: fetchLabel(
        programTemplateSiteCoreKeys.result,
        programTemplateSiteCoreKeys.countryHeader,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-counry",
      render: (row: any) => {
        const gridRef = React.createRef<HTMLDivElement>();

        const selectedCountries: ChipOptionsDataType[] = row?.countryName?.map(
          (countryName: string) => ({
            label: countryName,
            value: countryName,
          }),
        );

        const className = selectedCountries.length > 1 ? "nestedElement" : "";

        return (
          <MuiBox ref={gridRef} className={className}>
            <MuiChipWithPopover
              data={selectedCountries}
              handleDelete={() => {}}
              isEditable={false}
              headerLabel={commonLabels.selectedCountries}
              gridRef={gridRef}
              countToWrap={1}
              showMoreARIALabel={commonARIALabel.showNMore}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
            />
          </MuiBox>
        );
      },
    },

    {
      id: SearchProgramTemplateResultFormKey.createdDate,
      label: commonLabels.createdDate,
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {convertToUserFormat(row.createdDate, userCountryDateFormat)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    ...(!hasModulePermission(MODULE_PERMISSIONS.PROGRAM_TEMPLATE_EDIT)
      ? []
      : [
          {
            id: "edit",
            label: commonLabels.edit,
            render: (row: RowProps) => {
              return (
                <MuiLink
                  onClick={() =>
                    navigate(`${PAGE_URLS.EDIT_PROGRAM_TEMPLATE}/${row.id}`)
                  }
                >
                  <img src={Edit} alt={commonLabels.edit + " " + row.name} />
                </MuiLink>
              );
            },
          },
        ]),
  ];

  const tableHeaderForm = [
    <FormController
      key="name"
      control={control}
      name={SearchProgramTemplateResultFormKey.name}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramTemplateResultFormKey.name}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key="lineOfBusiness"
      control={control}
      name={SearchProgramTemplateResultFormKey.lineOfBusiness}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueLineOfBusinesses}
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${SearchProgramTemplateResultFormKey.lineOfBusiness}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key="programFrameWork"
      control={control}
      name={SearchProgramTemplateResultFormKey.programFrameWork}
    >
      <MuiSelect
        label=""
        options={uniqueProgramFrameWorks}
        visibleDefaultSelectOption={true}
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${SearchProgramTemplateResultFormKey.programFrameWork}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key="solutionCategory"
      control={control}
      name={SearchProgramTemplateResultFormKey.solutionCategory}
    >
      <MuiSelect
        label=""
        options={uniqueSolutionCategories}
        visibleDefaultSelectOption={true}
        placeholder={commonLabels.selectPlaceHolder}
        value=""
        id={`${pageId}-${SearchProgramTemplateResultFormKey.solutionCategory}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key="solutionCategoryLob"
      control={control}
      name={SearchProgramTemplateResultFormKey.solutionCategoryLob}
    >
      <MuiSelect
        label=""
        options={uniqueSolutionCategoryLobs}
        visibleDefaultSelectOption={true}
        placeholder={commonLabels.selectPlaceHolder}
        value=""
        id={`${pageId}-${SearchProgramTemplateResultFormKey.solutionCategoryLob}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key="status"
      control={control}
      name={SearchProgramTemplateResultFormKey.status}
    >
      <MuiSelect
        label=""
        placeholder={commonLabels.selectPlaceHolder}
        options={fetchDropdownValues(
          programTemplateSiteCoreKeys.searchFormKey,
          programTemplateSiteCoreKeys.statusList,
          true,
        )}
        visibleDefaultSelectOption={false}
        value=""
        id={`${pageId}-${SearchProgramTemplateResultFormKey.status}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key="countryName"
      control={control}
      name={SearchProgramTemplateResultFormKey.countryName}
      ref={countryRef}
    >
      <MuiMultiSelect
        label=""
        disabled={false}
        showSearch={true}
        showChip={false}
        textFieldWidth={0}
        options={uniqueCountries}
        id={`${pageId}-${SearchProgramTemplateResultFormKey.countryName}`}
        selectPlaceHolder={commonLabels.selectPlaceHolder}
        searchPlaceHolder={commonLabels.search}
        selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
        doneText={commonLabels.done}
        allARIAProps={multiSelectARIALabels}
      />
    </FormController>,
    <FormController
      key="createdDate"
      control={control}
      name={SearchProgramTemplateResultFormKey.createdDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          name="createdDate"
          clearable
          value={
            getValues(SearchProgramTemplateResultFormKey.createdDate) !== ""
              ? dayjs(
                  getValues(SearchProgramTemplateResultFormKey.createdDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              SearchProgramTemplateResultFormKey.createdDate,
              newValue?.format(userCountryDateFormat) || "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    ...(!hasModulePermission(MODULE_PERMISSIONS.PROGRAM_TEMPLATE_EDIT)
      ? []
      : [<></>]),
  ];

  return (
    <ProgramTemplateSearchResultWrapper>
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }} className="form-header">
          <MuiTypography variant="h2SemiBold" className="form-header-left">
            {fetchComponentTitle(programTemplateSiteCoreKeys.result)}
          </MuiTypography>
        </MuiGrid>
        <MuiGrid container>
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={tableHeaderForm}
              columns={columns}
              tableResults={[...filteredResults]}
              noDataInApi={isSearch}
              priorityResults={[]}
              isLastColumnSticky={hasModulePermission(
                MODULE_PERMISSIONS.PROGRAM_TEMPLATE_EDIT,
              )}
              // noResultMessage={commonLabels.searchCriteria}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
    </ProgramTemplateSearchResultWrapper>
  );
};
